import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';
import { api } from '@/client/api';
import { refreshDemoData } from '@/client/DataProvider';

/** Account linking and lifecycle (mandate §1) — the model half of the composed AccountLink surface.
 *
 *  The server has owned the whole flow since DEC-108 — slot-gated link, secret-stripping params, idempotent
 *  verify that creates the account, unlink — and the UI never called it. This slice is that call, with the
 *  provider registry the modal renders: PER-PROVIDER, NON-SECRET forms only, because the contract's own words
 *  are the security boundary ("secrets never travel through procedures — the link flow hands off to the
 *  broker's own auth", §19). Nothing here asks for, stores, logs or transmits a password.
 *
 *  AND THE FLOW SAYS WHAT IT IS: no live broker link exists in this build — the broker rails place orders;
 *  nothing authenticates AS the trader at their broker — so verify records a SIMULATED SHELL, contacts
 *  nothing, reads no balance, and every surface says so (the tester who typed a real account number and saw a
 *  fabricated $10,000 was right to call it fake; that number is gone). The §19 handoff is the contract's
 *  DESIGN for the live integration, not a claim about this build.
 *
 *  MT4 and MT5 ride the `metaapi` broker with a non-secret `platform` param — one server rail, two flows the
 *  trader recognises. The registry is data, so a broker the server grows next appears by adding a row.
 */

export interface LinkField {
  readonly key: string;
  /** Secret fields ride the VERIFY call only (write-only in the contract) — never the link row, never state
   *  that outlives the dialog. Rendered as password inputs. */
  readonly secret?: boolean;
  readonly label: string;
  readonly placeholder: string;
  readonly hint?: string;
}
export interface LinkProvider {
  readonly id: string; // registry key ('mt4' and 'mt5' both map to broker 'metaapi')
  readonly broker: 'tradelocker' | 'metaapi' | 'ctrader' | 'fix';
  readonly label: string;
  readonly blurb: string;
  readonly fixedParams: Readonly<Record<string, string>>;
  readonly fields: readonly LinkField[];
}

// A provider REGISTRY, not display data: each row maps the server's broker enum to its non-secret form
// fields. The strings are field labels/hints, and the sim dataset is the wrong home for configuration the
// live binding needs identically.
// data-lint-allow: provider registry (configuration), not display data
export const LINK_PROVIDERS: readonly LinkProvider[] = [
  {
    id: 'mt4',
    broker: 'metaapi',
    label: 'MetaTrader 4',
    blurb:
      'On a deployment with MetaApi access, verification deploys the account at MetaApi with these details and reads its own figures back; without that access the link says so instead of pretending.',
    fixedParams: { platform: 'mt4' },
    fields: [
      { key: 'server', label: 'Broker server', placeholder: 'e.g. ICMarkets-Demo02' },
      { key: 'accountNumber', label: 'Account number', placeholder: 'e.g. 51234567' },
      {
        key: 'password',
        label: 'Account password',
        placeholder: 'used for the broker handshake only',
        secret: true,
      },
    ],
  },
  {
    id: 'mt5',
    broker: 'metaapi',
    label: 'MetaTrader 5',
    blurb:
      'On a deployment with MetaApi access, verification deploys the account at MetaApi with these details and reads its own figures back; without that access the link says so instead of pretending.',
    fixedParams: { platform: 'mt5' },
    fields: [
      { key: 'server', label: 'Broker server', placeholder: 'e.g. FTMO-Server' },
      { key: 'accountNumber', label: 'Account number', placeholder: 'e.g. 1520000123' },
      {
        key: 'password',
        label: 'Account password',
        placeholder: 'used for the broker handshake only',
        secret: true,
      },
    ],
  },
  {
    id: 'tradelocker',
    broker: 'tradelocker',
    label: 'TradeLocker',
    blurb:
      'On a deployment with the TradeLocker rail bound, verification signs in with these credentials and reads your account from the listing; without it the link says so instead of pretending.',
    fixedParams: {},
    fields: [
      {
        key: 'environment',
        label: 'Environment',
        placeholder: 'demo or live',
        hint: 'The private test links demo (paper) environments.',
      },
      { key: 'email', label: 'TradeLocker email', placeholder: 'you@example.com' },
      { key: 'server', label: 'Server', placeholder: 'e.g. OSP-DEMO' },
      {
        key: 'accountNumber',
        label: 'Account id (optional when you hold one account)',
        placeholder: 'e.g. 184745',
      },
      {
        key: 'password',
        label: 'TradeLocker password',
        placeholder: 'used for the broker handshake only',
        secret: true,
      },
    ],
  },
  {
    id: 'ctrader',
    broker: 'ctrader',
    label: 'cTrader',
    blurb:
      'cTrader-shaped details. Nothing is contacted in this build — no OAuth exists yet; the link is recorded as a simulated shell.',
    fixedParams: {},
    fields: [
      { key: 'environment', label: 'Environment', placeholder: 'demo or live' },
      { key: 'accountNumber', label: 'Account number', placeholder: 'e.g. 3001234' },
    ],
  },
  {
    id: 'fix',
    broker: 'fix',
    label: 'FIX bridge',
    blurb:
      'FIX connection identifiers only — session keys would be exchanged out of band. Nothing is contacted in this build; the link is recorded as a simulated shell.',
    fixedParams: {},
    fields: [
      { key: 'host', label: 'FIX host', placeholder: 'fix.broker.example:9823' },
      { key: 'senderCompId', label: 'SenderCompID', placeholder: 'e.g. PFIVE01' },
    ],
  },
];

export function buildLinking(c: Ctx) {
  const S = c.S;
  const L = S.acctLink;

  const close = () => c.set({ acctLink: null });

  const open = () =>
    c.set({
      acctLink: {
        tab: 'link',
        step: 'provider',
        providerId: null,
        params: {},
        secrets: {},
        nickname: '',
        busy: false,
        error: null,
        linkId: null,
        message: null,
        unlinkConfirm: null,
      },
    });

  const patch = (part: Partial<NonNullable<typeof L>>) =>
    c.set((st) => (st.acctLink ? { acctLink: { ...st.acctLink, ...part } } : {}));

  const provider = L?.providerId ? (LINK_PROVIDERS.find((p) => p.id === L.providerId) ?? null) : null;

  const linkGo = async () => {
    if (!L || !provider || L.busy) return;
    // Client-side validation FIRST: every field present, nickname present. The server validates again — two
    // fences, one message the trader can act on.
    const missing = provider.fields.filter(
      (f) =>
        !((f.secret ? L.secrets[f.key] : L.params[f.key]) ?? '').trim() &&
        !f.label.toLowerCase().includes('optional'),
    );
    if (!L.nickname.trim() || missing.length) {
      patch({
        error: !L.nickname.trim()
          ? 'Give the account a nickname.'
          : `Fill in ${missing.map((f) => f.label.toLowerCase()).join(', ')}.`,
      });
      return;
    }
    patch({ busy: true, error: null });
    const key = `link-${provider.id}-${Date.now()}`;
    try {
      const linked = await api.manage.accounts.link({
        idempotencyKey: key,
        broker: provider.broker,
        // Non-secret fields only — secrets ride the verify call, write-only, and never a stored row.
        params: { ...provider.fixedParams, ...L.params },
        nickname: L.nickname.trim(),
      });
      patch({ busy: false, step: 'verify', linkId: linked.linkId, message: linked.message });
    } catch (error) {
      patch({
        busy: false,
        error: error instanceof Error ? error.message : 'The link could not be started.',
      });
    }
  };

  const verifyGo = async () => {
    if (!L?.linkId || L.busy) return;
    patch({ busy: true, error: null });
    try {
      const verified = await api.manage.accounts.verify({
        idempotencyKey: `verify-${L.linkId}`, // deterministic per link: a retried verify RESUMES, never duplicates
        linkId: L.linkId,
        ...(Object.keys(L.secrets).length ? { secrets: L.secrets } : {}),
      });
      if (verified.state === 'verified') {
        patch({ busy: false, step: 'done', message: verified.message });
        // The new account appears immediately, everywhere the list feeds (mandate §1's acceptance).
        refreshDemoData();
        c.flash(`${L.nickname || 'Account'} connected.`, 'pos');
      } else {
        patch({ busy: false, error: verified.message || 'The connection was not confirmed. Try again.' });
      }
    } catch (error) {
      patch({
        busy: false,
        error: error instanceof Error ? error.message : 'Verification failed. Try again.',
      });
    }
  };

  const unlinkGo = async (accountId: string, label: string) => {
    if (L?.busy) return;
    patch({ busy: true, error: null });
    try {
      await api.manage.accounts.unlink({ idempotencyKey: `unlink-${accountId}-${Date.now()}`, accountId });
      patch({ busy: false, unlinkConfirm: null });
      refreshDemoData();
      c.flash(`${label} unlinked. Its history stays in the ledger.`, 'mut');
    } catch (error) {
      patch({
        busy: false,
        error: error instanceof Error ? error.message : 'The account could not be unlinked.',
      });
    }
  };

  // The manage tab's rows: the ACCOUNTS LIST the server already serves (authoritative — the same rows the
  // cards render), with health fetched live when the tab opens, so "connected" is a REPORTED state.
  const manageRows = c.data.core.accounts.map((a) => {
    const h = L?.health?.find((x) => x.accountId === a.id) ?? null;
    return {
      id: a.id,
      label: a.firm,
      plan: a.plan,
      sync: h?.sync ?? null,
      simulated: /simulated/i.test(a.plan),
    };
  });

  const loadHealth = async () => {
    try {
      const { items } = await api.manage.accounts.health();
      patch({ health: items });
    } catch {
      patch({ health: [] }); // health unknown is shown as unknown, never guessed
    }
  };

  return {
    acctLinkOpen: open,
    acctLinkState: L,
    acctLinkProviders: LINK_PROVIDERS,
    acctLinkProvider: provider,
    acctLinkClose: close,
    acctLinkTab: (tab: 'link' | 'manage') => {
      patch({ tab });
      if (tab === 'manage') void loadHealth();
    },
    acctLinkPick: (id: string) => patch({ providerId: id, step: 'form', params: {}, error: null }),
    acctLinkBack: () => patch({ step: 'provider', error: null }),
    acctLinkSetParam: (key: string, value: string) =>
      c.set((st) => {
        if (!st.acctLink) return {};
        const secret = LINK_PROVIDERS.some((p) => p.fields.some((f) => f.key === key && f.secret));
        return secret
          ? { acctLink: { ...st.acctLink, secrets: { ...st.acctLink.secrets, [key]: value } } }
          : { acctLink: { ...st.acctLink, params: { ...st.acctLink.params, [key]: value } } };
      }),
    acctLinkSetNickname: (value: string) => patch({ nickname: value }),
    acctLinkGo: linkGo,
    acctLinkVerify: verifyGo,
    acctLinkRows: manageRows,
    acctLinkUnlinkAsk: (id: string, label: string) => patch({ unlinkConfirm: { id, label } }),
    acctLinkUnlinkCancel: () => patch({ unlinkConfirm: null }),
    acctLinkUnlinkGo: unlinkGo,
    acctLinkRefresh: () => {
      refreshDemoData();
      c.flash('Refreshing accounts from the ledger…', 'mut');
    },
  } satisfies Partial<ViewModel>;
}
